import { useCallback, useMemo, useRef, useState } from 'react'

import { Box, Typography } from '@material-ui/core'
import Slider from '@material-ui/core/Slider'
import { random } from 'lodash'

const getLabel = (value: number) => `${value}`

export enum RangeValueType {
  FROM,
  TO,
}

type RangeProps = {
  onChange: (value: number[]) => void
  from: number
  to: number
  min: number
  max: number
  title?: string
  step?: number
  getAriaValueText?: (value: number) => string
  fixedLabels?: boolean
  fixedLabelsWithMarks?: boolean
  formatLabel?: (value: number, type?: RangeValueType, showDetails?: boolean) => string
  hideValueLabel?: boolean
  disabled?: boolean
  allowOverlap?: boolean
}

const Range = ({
  from,
  to,
  title,
  onChange,
  min,
  max,
  step = 1,
  getAriaValueText,
  fixedLabels = false,
  fixedLabelsWithMarks = false,
  formatLabel = getLabel,
  hideValueLabel = false,
  disabled = false,
  allowOverlap = false,
}: RangeProps) => {
  const [displayValue, setDisplayValue] = useState([from, to])
  const lastValue = useRef([from, to])
  const defaultValue = useRef([from, to])
  const id = useMemo(() => `range-slider-${random(1, 2000)}`, [])
  const fromDisplay = displayValue[0]
  const toDisplay = displayValue[1]
  const marks =
    fixedLabels || fixedLabelsWithMarks
      ? fixedLabelsWithMarks
      : [
          {
            value: fromDisplay,
            label: formatLabel(fromDisplay, RangeValueType.FROM),
          },
          {
            value: toDisplay,
            label: formatLabel(toDisplay, RangeValueType.TO),
          },
        ]
  const onSelect = useCallback(() => {
    const fromChanged = lastValue.current[0] !== displayValue[0]
    const toChanged = lastValue.current[1] !== displayValue[1]
    const hasChanged = fromChanged || toChanged
    if (hasChanged) {
      onChange(displayValue)
      lastValue.current = displayValue
    }
  }, [onChange, displayValue])

  const changeDisplay = useCallback(
    (_, value) => {
      const sameValues = value[0] === value[1]
      if (allowOverlap || !sameValues) {
        setDisplayValue(value)
      }
    },
    [allowOverlap],
  )

  return (
    <Box>
      <Box
        ml={-1.5}
        mb={1}
      >
        <Typography
          id={id}
          gutterBottom
        >
          {title}
        </Typography>
      </Box>
      <Slider
        aria-labelledby={id}
        valueLabelFormat={(value) => getLabel(value)}
        defaultValue={defaultValue.current}
        marks={marks}
        valueLabelDisplay={hideValueLabel ? 'off' : 'auto'}
        onChangeCommitted={() => {
          onSelect()
        }}
        onChange={changeDisplay}
        min={min}
        max={max}
        step={step}
        getAriaValueText={getAriaValueText}
        value={displayValue}
        disabled={disabled}
      />
      {(fixedLabels || fixedLabelsWithMarks) && (
        <Box
          display="flex"
          justifyContent={fromDisplay === toDisplay ? 'center' : 'space-between'}
        >
          <Typography
            variant="body2"
            color="textPrimary"
          >
            {formatLabel(fromDisplay, RangeValueType.FROM)}
          </Typography>
          {fromDisplay !== toDisplay && (
            <Typography
              variant="body2"
              color="textPrimary"
            >
              {formatLabel(toDisplay, RangeValueType.TO)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export default Range
